import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import {
    DashboardPreset,
    DashboardSummaryRequest,
    DashboardSummaryResponse,
    getDashboardSummary,
} from '../../services/dashboardService';

export interface DashboardFilters {
    preset: DashboardPreset;
    fromDate: string | null;
    toDate: string | null;
}

export interface DashboardState {
    filters: DashboardFilters;
    data: DashboardSummaryResponse | null;
    loading: boolean;
    error: string | null;
}

const initialState: DashboardState = {
    filters: {
        preset: 'last_30_days',
        fromDate: null,
        toDate: null,
    },
    data: null,
    loading: false,
    error: null,
};

export const fetchDashboardSummary = createAsyncThunk<
    DashboardSummaryResponse,
    DashboardSummaryRequest,
    { rejectValue: string }
>('dashboard/fetchSummary', async (params, { rejectWithValue }) => {
    const request: DashboardSummaryRequest = {
        preset: params.preset ?? 'last_30_days',
        from: params.from,
        to: params.to,
    };

    try {
        const response = await getDashboardSummary(request);
        return response;
    } catch (error: unknown) {
        const message =
            error instanceof Error
                ? error.message
                : 'Failed to load dashboard summary';
        return rejectWithValue(message);
    }
});

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        setPreset(state, action: PayloadAction<DashboardPreset>) {
            state.filters.preset = action.payload;
            if (action.payload !== 'custom') {
                state.filters.fromDate = null;
                state.filters.toDate = null;
            }
        },
        setCustomRange(
            state,
            action: PayloadAction<{ from: string; to: string }>
        ) {
            state.filters.preset = 'custom';
            state.filters.fromDate = action.payload.from;
            state.filters.toDate = action.payload.to;
        },
        clearCustomRange(state) {
            state.filters = {
                preset: 'last_30_days',
                fromDate: null,
                toDate: null,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDashboardSummary.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                fetchDashboardSummary.fulfilled,
                (state, action: PayloadAction<DashboardSummaryResponse>) => {
                    state.loading = false;
                    state.data = action.payload;
                }
            )
            .addCase(
                fetchDashboardSummary.rejected,
                (state, action) => {
                    state.loading = false;
                    state.error =
                        action.payload ?? 'Failed to load dashboard summary';
                }
            );
    },
});

export const { setPreset, setCustomRange, clearCustomRange } =
    dashboardSlice.actions;

export const selectDashboardState = (state: RootState) => state.dashboard;

export default dashboardSlice.reducer;


